import type { Moved } from "../protocol";
import { tableScale } from "./table-scale";

// A card that changes zones is a new object (400.7), so the board can't follow it by its object id.
// The Room says which object each one the viewer saw move became (StateUpdate's Moved list); the
// board draws the new state at once and flies each new object's node from where the old one was
// drawn (FLIP). Nothing waits on a flight: the board is live underneath, and any press or key lands
// every card still flying (ArenaBoard).

const FLIGHT_MS = 320;
const STAGGER_MS = 110;
// However many cards one update moved, the last starts flying by then.
const BURST_SPREAD_MS = 600;
const EASE_OUT = "cubic-bezier(0.2, 0.8, 0.2, 1)";

// Where a card that moved was drawn before the update, by the object it became, in the order the
// moves happened. A card that moved twice in one update (cast, then resolved) flies once, from where
// it was drawn before both.
export type Takeoff = { to_object: number; from: DOMRect };

export function measureTakeoffs(root: ParentNode, moves: Moved[]): Takeoff[] {
  const origin = new Map<number, number>();
  const landings = new Map<number, number>();
  for (const m of moves) {
    const first = origin.get(m.from_object) ?? m.from_object;
    origin.set(m.to_object, first);
    landings.delete(m.to_object);
    landings.set(m.to_object, first);
  }
  const out: Takeoff[] = [];
  for (const [to_object, first] of landings) {
    const node = drawnObject(root, first);
    if (node) out.push({ to_object, from: node.getBoundingClientRect() });
  }
  return out;
}

// Each takeoff whose object is drawn now flies from where it was, one after another. A card that
// left the board's sight in between (an intermediate object, or one now in a hidden zone) doesn't.
export function fly(root: ParentNode, takeoffs: Takeoff[], holdMs = 0): Animation[] {
  if (takeoffs.length === 0 || matchMedia("(prefers-reduced-motion: reduce)").matches) return [];
  const k = tableScale(root);
  const legs = takeoffs.flatMap((t) => {
    const node = flyingNode(root, t.to_object);
    if (!node) return [];
    const to = node.getBoundingClientRect();
    const dx = (t.from.left + t.from.width / 2 - (to.left + to.width / 2)) / k;
    const dy = (t.from.top + t.from.height / 2 - (to.top + to.height / 2)) / k;
    return to.width > 0 && to.height > 0 && (Math.abs(dx) >= 2 || Math.abs(dy) >= 2) ? [{ node, from: t.from, to, dx, dy }] : [];
  });
  const stagger = legs.length > 1 ? Math.min(STAGGER_MS, BURST_SPREAD_MS / (legs.length - 1)) : 0;
  // The individual translate and scale properties compose with the node's own transform (a hand
  // card's tilt, an attacker's lift), which a transform keyframe would replace. One scale for both
  // axes, fitting the node inside where it was: a tile cropped wide would stretch to a card's shape.
  return legs.map((leg, i) => {
    const flight = leg.node.animate([
      { translate: `${leg.dx}px ${leg.dy}px`, scale: `${Math.min(leg.from.width / leg.to.width, leg.from.height / leg.to.height)}`, zIndex: 40 },
      { translate: "0px 0px", scale: "1", zIndex: 40 },
    ], { duration: FLIGHT_MS, delay: holdMs + i * stagger, easing: EASE_OUT, fill: "backwards" });
    // A card flying past the pointer isn't hovered: it would pop up its preview, or fade the other
    // stack items, as it went by.
    leg.node.style.pointerEvents = "none";
    const landed = () => leg.node.style.removeProperty("pointer-events");
    flight.finished.then(landed, landed);
    return flight;
  });
}

// Ends every flight where it lands.
export function land(flights: Animation[]): void {
  for (const f of flights) f.finish();
}

function drawnObject(root: ParentNode, oid: number): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-anchor~="obj:${oid}"]`);
}

// A battlefield slot drawing several like permanents as one pile flies only its top card in.
function flyingNode(root: ParentNode, oid: number): HTMLElement | null {
  const node = drawnObject(root, oid);
  if (!node || !node.classList.contains("bf-slot") || (node.dataset["anchor"] ?? "").split(" ").length < 2) return node;
  return node.querySelector<HTMLElement>(":scope > card-art") ?? node;
}
